<template>
  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
    <div class="bg-white p-4 rounded-lg shadow">
      <h3 class="text-gray-500 text-sm">
        Total Programs
      </h3>
      <p class="text-2xl font-bold">
        {{ totalPrograms }}
      </p>
    </div>
    <div class="bg-white p-4 rounded-lg shadow">
      <h3 class="text-gray-500 text-sm">
        Free Programs
      </h3>
      <p class="text-2xl font-bold">
        {{ freePrograms }}
      </p>
    </div>
    <div class="bg-white p-4 rounded-lg shadow">
      <h3 class="text-gray-500 text-sm">
        Paid Programs
      </h3>
      <p class="text-2xl font-bold">
        {{ paidPrograms }}
      </p>
    </div>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
    <div class="bg-white p-4 rounded-lg shadow">
      <h3 class="text-gray-500 text-sm mb-4">
        Program Categories
      </h3>
      <div class="h-64">
        <canvas ref="categoryChart" />
      </div>
    </div>
    <div class="bg-white p-4 rounded-lg shadow">
      <h3 class="text-gray-500 text-sm mb-4">
        Top Programs by Usage
      </h3>
      <div class="h-64">
        <canvas ref="programUsageChart" />
      </div>
    </div>
  </div>

  <div class="bg-white rounded-lg shadow overflow-hidden">
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Program
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Category
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Developer
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Publisher
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              License Type
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Usage Count
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="program in programs" :key="program.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ program.name }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ program.category }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ program.developer }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ program.publisher }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ program.licenseType }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ program.usageCount }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import Chart from 'chart.js/auto';

const props = defineProps({
  programs: {
    type: Array,
    default: () => []
  },
  stats: {
    type: Object,
    default: () => ({})
  }
});

const categoryChart = ref(null);
const programUsageChart = ref(null);

const totalPrograms = computed(() => props.stats.total || 0);
const freePrograms = computed(() => props.stats.free || 0);
const paidPrograms = computed(() => props.stats.paid || 0);

onMounted(() => {
  // Create category distribution chart
  if (props.stats.categories) {
    new Chart(categoryChart.value, {
      type: 'pie',
      data: {
        labels: props.stats.categories.map(c => c.name),
        datasets: [ {
          data: props.stats.categories.map(c => c.total),
          backgroundColor: [
            '#FF6384',
            '#36A2EB',
            '#FFCE56',
            '#4BC0C0',
            '#9966FF',
            '#FF9F40'
          ]
        } ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  // Create program usage chart
  if (props.programs.length) {
    const topPrograms = [ ...props.programs ]
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 5);

    new Chart(programUsageChart.value, {
      type: 'bar',
      data: {
        labels: topPrograms.map(p => p.name),
        datasets: [ {
          label: 'Usage Count',
          data: topPrograms.map(p => p.usageCount),
          backgroundColor: '#36A2EB'
        } ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true
          }
        }
      }
    });
  }
});
</script>
